import { useState } from "react";
import { usePlatformBranding } from "@/hooks/usePlatformBranding";
import { cn } from "@/lib/utils";

export type BrandLogoVariant = "full" | "compact" | "mark";
export type BrandLogoSize = "xs" | "sm" | "md" | "lg" | "xl" | "hero";

interface BrandLogoProps {
  /** 
   * Variant determines what to show:
   * - full: Full wordmark logo (for landing/auth pages)
   * - compact: Mark + name (for nav bars)
   * - mark: Symbol only (for sidebars/mobile)
   */
  variant?: BrandLogoVariant;
  size?: BrandLogoSize;
  className?: string;
  /** Whether to show platform name text (only applies when no logo is set) */
  showName?: boolean;
}

const sizeConfig: Record<BrandLogoSize, { 
  logoHeight: string; 
  iconSize: number; 
  textSize: string;
  maxWidth: string;
}> = {
  xs: { logoHeight: "h-5", iconSize: 20, textSize: "text-sm", maxWidth: "max-w-[80px]" },
  sm: { logoHeight: "h-7", iconSize: 24, textSize: "text-lg", maxWidth: "max-w-[120px]" },
  md: { logoHeight: "h-8", iconSize: 32, textSize: "text-xl", maxWidth: "max-w-[160px]" },
  lg: { logoHeight: "h-10", iconSize: 40, textSize: "text-2xl", maxWidth: "max-w-[200px]" },
  xl: { logoHeight: "h-14", iconSize: 48, textSize: "text-3xl", maxWidth: "max-w-[240px]" },
  hero: { logoHeight: "h-14 md:h-20", iconSize: 48, textSize: "text-3xl", maxWidth: "max-w-[280px]" },
};

/**
 * Unified brand logo component - uses platform settings from database.
 * Falls back to platform name text when no logo is configured.
 */
export function BrandLogo({
  variant = "compact",
  size = "md",
  className,
  showName = true,
}: BrandLogoProps) {
  const { logoUrl, markLogoUrl, platformName, isLoading } = usePlatformBranding();
  const [logoError, setLogoError] = useState(false);
  const [markError, setMarkError] = useState(false);
  const config = sizeConfig[size];

  // Loading skeleton
  if (isLoading) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <div 
          className={cn(
            "bg-muted/50 rounded animate-pulse",
            variant === "mark" ? "aspect-square" : "w-24",
            config.logoHeight
          )} 
        />
        {variant !== "mark" && showName && (
          <div className="h-5 w-20 bg-muted/50 rounded animate-pulse hidden sm:block" />
        )}
      </div>
    );
  }

  // Text fallback when no logo available
  const renderNameFallback = (showText: boolean = true) => (
    showText && showName ? (
      <span className={cn(
        "font-display font-bold tracking-wide bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent",
        config.textSize
      )}>
        {platformName}
      </span>
    ) : (
      <span className={cn(
        "font-display font-bold tracking-wide bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent",
        config.textSize
      )}>
        {platformName.charAt(0)}
      </span>
    )
  );

  // Determine what to render based on variant and available assets
  const renderContent = () => {
    switch (variant) {
      case "full":
        // Full wordmark - prefer logoUrl, fallback to mark + name, then just name
        if (logoUrl && !logoError) {
          return (
            <img
              src={logoUrl}
              alt={platformName}
              className={cn("w-auto object-contain", config.logoHeight, config.maxWidth)}
              onError={() => setLogoError(true)}
            />
          );
        }
        // Fallback: mark + name or just name
        return (
          <>
            {markLogoUrl && !markError ? (
              <img 
                src={markLogoUrl} 
                alt="" 
                className={cn("object-contain", config.logoHeight)}
                width={config.iconSize}
                height={config.iconSize}
                style={{ width: 'auto', height: config.iconSize }}
                onError={() => setMarkError(true)}
              />
            ) : null}
            {showName && (
              <span className={cn(
                "font-display font-bold tracking-wide bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent",
                config.textSize
              )}>
                {platformName}
              </span>
            )}
          </>
        );

      case "compact":
        // Compact: prefer mark logo + name, or full logo if no mark
        if (logoUrl && !markLogoUrl && !logoError) {
          // Only have full logo - use it
          return (
            <img
              src={logoUrl}
              alt={platformName}
              className={cn("w-auto object-contain", config.logoHeight)}
              onError={() => setLogoError(true)}
            />
          );
        }
        // Have mark logo or using fallback
        return (
          <>
            {markLogoUrl && !markError ? (
              <img 
                src={markLogoUrl} 
                alt="" 
                className="object-contain"
                width={config.iconSize}
                height={config.iconSize}
                style={{ height: config.iconSize, width: 'auto' }}
                onError={() => setMarkError(true)}
              />
            ) : null}
            {showName && (
              <span className={cn(
                "font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent",
                config.textSize
              )}>
                {platformName}
              </span>
            )}
          </>
        );

      case "mark":
        // Mark only - symbol/icon, fallback to first letter
        if (markLogoUrl && !markError) {
          return (
            <img 
              src={markLogoUrl} 
              alt={platformName} 
              className="object-contain"
              width={config.iconSize}
              height={config.iconSize}
              style={{ height: config.iconSize, width: config.iconSize }}
              onError={() => setMarkError(true)}
            />
          );
        }
        if (logoUrl && !logoError) {
          // Use full logo if no mark available
          return (
            <img
              src={logoUrl}
              alt={platformName}
              className={cn("w-auto object-contain", config.logoHeight)}
              style={{ maxHeight: config.iconSize }}
              onError={() => setLogoError(true)}
            />
          );
        }
        // Fallback to first letter of platform name
        return renderNameFallback(false);
    }
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {renderContent()}
    </div>
  );
}

// Re-export for backward compatibility
export { BrandLogo as PlatformLogo };
